import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, Avatar, message, Modal, Space, Typography, Divider, Tag, Select, Switch, DatePicker } from 'antd';
import { UserOutlined, CameraOutlined, LockOutlined, SafetyOutlined, SaveOutlined, EditOutlined, BellOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';
import ReactCountryFlag from 'react-country-flag';
import type { UploadFile } from 'antd/es/upload/interface';
import { useDashboard } from '../../../context/DashboardContext';
import LoadingButton from '../../common/LoadingButton';
import { useApiLoading } from '../../../hooks/useApiLoading';

const { Title, Text } = Typography;
const { Option } = Select;

// Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

// Country list with ISO codes
const COUNTRIES = [
  { code: 'NG', name: 'Nigeria' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'IE', name: 'Ireland' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'EG', name: 'Egypt' },
  { code: 'IN', name: 'India' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'PH', name: 'Philippines' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'IL', name: 'Israel' },
  { code: 'TR', name: 'Turkey' },
  { code: 'RU', name: 'Russia' },
];

const ProfileContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  color: ${COLORS.textPrimary};
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  border-radius: 12px !important;
  
  .ant-card-head {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-card-head-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary};
  }
  
  label {
    color: ${COLORS.textSecondary} !important;
  }
  
  .ant-input,
  .ant-select-selector {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px !important;
    
    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input::placeholder {
    color: #64748B !important;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-close {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.textPrimary} !important;
    }
  }
  
  .ant-modal-body {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-form-item-label > label {
    color: ${COLORS.textSecondary} !important;
  }
  
  .ant-input,
  .ant-input-password,
  .ant-select-selector,
  .ant-input-number,
  .ant-input-number-input,
  .ant-select-selection-search-input {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    
    &:hover,
    &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input::placeholder,
  .ant-select-selection-placeholder,
  .ant-input-number-input::placeholder {
    color: #64748B !important;
  }
  
  .ant-input-number-handler-wrap {
    background: ${COLORS.bgSecondary} !important;
    border-left: 1px solid ${COLORS.border} !important;
  }
  
  .ant-input-number-handler {
    color: ${COLORS.textSecondary} !important;
    
    &:hover {
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-select-dropdown {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
  }
  
  .ant-select-item {
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgSecondary} !important;
    
    &:hover {
      background: ${COLORS.hover} !important;
    }
    
    &.ant-select-item-option-selected {
      background: ${COLORS.accentDim} !important;
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-btn {
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    background: ${COLORS.bgPrimary} !important;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
    }
  }
  
  .ant-btn-primary {
    background: ${COLORS.accent} !important;
    border-color: ${COLORS.accent} !important;
    color: ${COLORS.bgPrimary} !important;
    
    &:hover {
      background: #67e8f9 !important;
      border-color: #67e8f9 !important;
    }
  }
`;

const PortraitSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const PortraitPreview = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const StyledAvatar = styled(Avatar)`
  border: 3px solid ${COLORS.accent};
  box-shadow: 0 4px 12px rgba(34, 211, 238, 0.3);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const UploadButton = styled(Button)`
  background: ${COLORS.accent} !important;
  border-color: ${COLORS.accent} !important;
  color: ${COLORS.bgPrimary} !important;
  font-weight: 600;
  
  &:hover {
    background: #67e8f9 !important;
    border-color: #67e8f9 !important;
  }
  
  &:disabled {
    background: ${COLORS.border} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textSecondary} !important;
  }
`;

const ProfileInfoDisplay = styled.div`
  padding: 24px;
  
  .info-row {
    display: flex;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid ${COLORS.border};
    
    &:last-child {
      border-bottom: none;
    }
    
    .label {
      color: ${COLORS.textSecondary};
      font-weight: 500;
    }
    
    .value {
      color: ${COLORS.textPrimary};
    }
  }
  
  @media (max-width: 768px) {
    padding: 16px;
    
    .info-row {
      flex-direction: column;
      gap: 4px;
    }
  }
`;

const Profile: React.FC = () => {
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [criticalOptOut, setCriticalOptOut] = useState(false);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [portraitFile, setPortraitFile] = useState<UploadFile | null>(null);
  const [portraitPreview, setPortraitPreview] = useState<string | null>(null);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [reset2FAVisible, setReset2FAVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [passwordForm] = Form.useForm();
  const [reset2FAForm] = Form.useForm();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userProfile } = useDashboard();
  const apiLoading = useApiLoading();

  useEffect(() => {
    fetchProfile();
  }, []);

  // Update context when profile changes
  useEffect(() => {
    if (profile && userProfile) {
      // Trigger a refresh of the header by updating context
      // This will be handled by the parent component
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_ACCOUNT_PROFILE);
      if (response.data?.status === 'success' && response.data?.data) {
        const profileData = response.data.data;
        setProfile(profileData);
        setCriticalOptOut(Boolean(profileData.criticalNotificationsOptOut));
        if (profileData.portraitUrl) {
          setPortraitPreview(`data:image/jpeg;base64,${profileData.portraitUrl}`);
        }
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch profile');
    }
  };

  const handleSaveNotificationPreference = async () => {
    try {
      setNotificationSaving(true);
      const nextValue = !criticalOptOut;
      await apiClient.put(API_ENDPOINTS.ADMIN_UPDATE_ACCOUNT_PROFILE, {
        criticalNotificationsOptOut: nextValue,
      });
      setCriticalOptOut(nextValue);
      setNotificationModalVisible(false);
      message.success(
        nextValue
          ? 'You will stop receiving critical notification emails.'
          : 'Critical notification emails have been re-enabled.'
      );
    } catch (error: any) {
      message.error(
        error.response?.data?.message ||
          'Failed to update critical notification preference'
      );
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const resizeImage = (file: File, maxWidth: number = 800, maxHeight: number = 800, quality: number = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);
          const resizedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(resizedBase64);
        };
        img.onerror = reject;
        img.src = event.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        message.error('Please select a valid image file');
        return;
      }

      try {
        // Resize and compress the image
        const resizedBase64 = await resizeImage(file, 800, 800, 0.8);
        
        // Check the size of the resized image (base64 is ~33% larger than binary)
        const base64Data = resizedBase64.split(',')[1];
        const sizeInBytes = (base64Data.length * 3) / 4;
        
        if (sizeInBytes > 1024 * 1024) { // 1MB limit
          message.error('Image size too large. Please try a smaller image.');
          return;
        }

        setPortraitPreview(resizedBase64);
        setPortraitFile({ 
          uid: '-1',
          name: file.name,
          status: 'done',
          base64: base64Data 
        } as any);
      } catch (error) {
        console.error('Error processing image:', error);
        message.error('Failed to process image. Please try another image.');
      }
    }
  };

  const handleUploadPortrait = async () => {
    if (!portraitFile) {
      message.warning('Please select an image first');
      return;
    }

    setUploading(true);
    try {
      const updateData: any = {
        portraitUrl: (portraitFile as any).base64,
      };

      const response = await apiClient.request({
        method: 'PUT',
        url: API_ENDPOINTS.ADMIN_UPDATE_ACCOUNT_PROFILE,
        data: updateData,
      });
      
      if (response.data?.status === 'success') {
        message.success('Portrait uploaded successfully');
        await fetchProfile();
        setPortraitFile(null);
        // Force page reload to update header
        window.location.reload();
      } else {
        message.error('Failed to upload portrait');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload portrait';
      // Check if it's a size-related error and provide a better message
      if (errorMessage.includes('Payload content length') || errorMessage.includes('maximum allowed')) {
        message.error('Image size too large. Please select a smaller image.');
      } else {
        message.error(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async (values: any) => {
    setLoading(true);
    try {
      const updateData: any = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        countryCode: values.countryCode,
        stateName: values.stateName,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : undefined,
      };

      const response = await apiClient.request({
        method: 'PUT',
        url: API_ENDPOINTS.ADMIN_UPDATE_ACCOUNT_PROFILE,
        data: updateData,
      });
      
      if (response.data?.status === 'success') {
        message.success('Profile updated successfully');
        setEditModalVisible(false);
        await fetchProfile();
        window.location.reload();
      } else {
        message.error('Failed to update profile');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    setLoading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_CHANGE_PASSWORD, {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        twoFaCode: parseInt(values.twoFaCode),
      });

      if (response.data?.status === 'success') {
        message.success('Password changed successfully. Please log in again.');
        setChangePasswordVisible(false);
        passwordForm.resetFields();
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/?userType=Administrator';
        }, 2000);
      } else {
        message.error('Failed to change password');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleReset2FA = async (values: any) => {
    setLoading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_RESET_2FA, {
        twoFaCode: parseInt(values.twoFaCode),
      });

      if (response.data?.status === 'success') {
        message.success('2FA has been reset successfully. Please log in again.');
        setReset2FAVisible(false);
        reset2FAForm.resetFields();
        setTimeout(() => {
          localStorage.clear();
          window.location.href = '/?userType=Administrator';
        }, 2000);
      } else {
        message.error('Failed to reset 2FA');
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to reset 2FA');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = () => {
    if (profile) {
      editForm.setFieldsValue({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber,
        countryCode: profile.countryCode,
        stateName: profile.stateName,
        dateOfBirth: profile.dateOfBirth ? dayjs(profile.dateOfBirth) : undefined,
      });
      setEditModalVisible(true);
    }
  };

  return (
    <ProfileContainer>
      <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 24 }}>
        Profile Settings
      </Title>

      {/* Portrait Section */}
      <SectionCard title="Profile Picture">
        <PortraitSection>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <PortraitPreview onClick={handleImageClick}>
            <StyledAvatar
              size={120}
              src={portraitPreview || undefined}
              icon={!portraitPreview && <UserOutlined />}
            />
          </PortraitPreview>
          {portraitPreview && (
            <Text style={{ color: COLORS.textSecondary, fontSize: '12px' }}>
              Click image to change
            </Text>
          )}
          <UploadButton
            type="primary"
            icon={<CameraOutlined />}
            onClick={handleUploadPortrait}
            loading={uploading}
            disabled={!portraitFile}
          >
            Upload Portrait
          </UploadButton>
        </PortraitSection>
      </SectionCard>

      {/* Profile Information Display */}
      <SectionCard 
        title="Profile Information"
        extra={
          <LoadingButton 
            icon={<EditOutlined />} 
            onClick={openEditModal}
            style={{ color: COLORS.accent, borderColor: COLORS.accent }}
            useGlobalApiLoading
          >
            Edit
          </LoadingButton>
        }
      >
        <ProfileInfoDisplay>
          <div className="info-row">
            <span className="label">Email</span>
            <span className="value">{profile?.email || '-'}</span>
          </div>
          <div className="info-row">
            <span className="label">First Name</span>
            <span className="value">{profile?.firstName || '-'}</span>
          </div>
          <div className="info-row">
            <span className="label">Last Name</span>
            <span className="value">{profile?.lastName || '-'}</span>
          </div>
          <div className="info-row">
            <span className="label">Phone Number</span>
            <span className="value">{profile?.phoneNumber || '-'}</span>
          </div>
          <div className="info-row">
            <span className="label">Country</span>
            <span className="value">
              {profile?.countryCode ? (
                <Space>
                  <ReactCountryFlag
                    countryCode={profile.countryCode}
                    svg
                    style={{ width: '1.5em', height: '1.5em' }}
                  />
                  {COUNTRIES.find(c => c.code === profile.countryCode)?.name || profile.countryCode}
                </Space>
              ) : '-'}
            </span>
          </div>
          <div className="info-row">
            <span className="label">State/Province</span>
            <span className="value">{profile?.stateName || '-'}</span>
          </div>
          <div className="info-row">
            <span className="label">Date of Birth</span>
            <span className="value">
              {profile?.dateOfBirth ? dayjs(profile.dateOfBirth).format('YYYY-MM-DD') : '-'}
            </span>
          </div>
          {profile?.role && (
            <div className="info-row">
              <span className="label">Role</span>
              <span className="value">
                <Tag color="cyan">{profile.role}</Tag>
              </span>
            </div>
          )}
          {profile?.permissions && Array.isArray(profile.permissions) && profile.permissions.length > 0 && (
            <div className="info-row">
              <span className="label">Permissions</span>
              <span className="value">
                <Space wrap>
                  {profile.permissions.map((perm: string, idx: number) => (
                    <Tag key={idx} color="blue">{perm}</Tag>
                  ))}
                </Space>
              </span>
            </div>
          )}
        </ProfileInfoDisplay>
      </SectionCard>

      {/* Critical notifications */}
      <SectionCard
        title="Critical Notifications"
        extra={
          <Button
            icon={<BellOutlined />}
            onClick={() => setNotificationModalVisible(true)}
            style={{ color: COLORS.accent, borderColor: COLORS.accent }}
          >
            Manage
          </Button>
        }
      >
        <ProfileInfoDisplay>
          <div className="info-row">
            <span className="label">Critical notification emails</span>
            <span className="value">
              {criticalOptOut ? 'Muted (you will not get critical emails)' : 'Enabled'}
            </span>
          </div>
        </ProfileInfoDisplay>
      </SectionCard>

      <Modal
        title="Critical notification emails"
        visible={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        footer={[
          <LoadingButton key="cancel" onClick={() => setNotificationModalVisible(false)} disabled={notificationSaving} useGlobalApiLoading={false}>
            Cancel
          </LoadingButton>,
          <LoadingButton
            key="save"
            type="primary"
            loading={notificationSaving}
            onClick={handleSaveNotificationPreference}
            useGlobalApiLoading={false}
          >
            {criticalOptOut ? 'Re-enable emails' : 'Unsubscribe'}
          </LoadingButton>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Control whether you receive system-critical notification emails. Muting stops future
            critical emails to your admin address; you can re-enable anytime.
          </Text>
          <Space align="center">
            <Switch
              checked={!criticalOptOut}
              onChange={(checked) => setCriticalOptOut(!checked)}
            />
            <Text>{!criticalOptOut ? 'Emails enabled' : 'Emails muted'}</Text>
          </Space>
        </Space>
      </Modal>

      {/* Edit Profile Modal */}
      <StyledModal
        title="Edit Profile"
        visible={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
        style={{ top: 50 }}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter your first name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter your last name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[{ required: true, message: 'Please enter your phone number' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="countryCode"
            label="Country"
            rules={[{ required: true, message: 'Please select your country' }]}
          >
            <Select
              showSearch
              placeholder="Select country"
              filterOption={(input, option) => {
                const country = COUNTRIES.find(c => c.code === option?.value);
                return country?.name.toLowerCase().includes(input.toLowerCase()) || false;
              }}
            >
              {COUNTRIES.map(country => (
                <Option key={country.code} value={country.code}>
                  <Space>
                    <ReactCountryFlag
                      countryCode={country.code}
                      svg
                      style={{ width: '1.2em', height: '1.2em' }}
                    />
                    {country.name}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="stateName"
            label="State/Province"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="dateOfBirth"
            label="Date of Birth"
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              placeholder="Select date of birth"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <LoadingButton type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading || apiLoading}>
                Save Changes
              </LoadingButton>
              <LoadingButton onClick={() => {
                setEditModalVisible(false);
                editForm.resetFields();
              }} useGlobalApiLoading>
                Cancel
              </LoadingButton>
            </Space>
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Security Settings */}
      <SectionCard title="Security Settings">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
              Two-Factor Authentication
            </Text>
            <Space>
              <Tag color={profile?.twoFactorEnabled ? 'green' : 'orange'}>
                {profile?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </Tag>
              {profile?.twoFactorEnabled && (
                <Button
                  icon={<SafetyOutlined />}
                  onClick={() => setReset2FAVisible(true)}
                  danger
                >
                  Reset 2FA
                </Button>
              )}
            </Space>
          </div>

          <Divider style={{ borderColor: COLORS.border, margin: '16px 0' }} />

          <div>
            <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 8 }}>
              Password
            </Text>
            <Button
              icon={<LockOutlined />}
              onClick={() => setChangePasswordVisible(true)}
            >
              Change Password
            </Button>
          </div>
        </Space>
      </SectionCard>

      {/* Change Password Modal */}
      <StyledModal
        title="Change Password"
        visible={changePasswordVisible}
        onCancel={() => {
          setChangePasswordVisible(false);
          passwordForm.resetFields();
        }}
        footer={null}
        style={{ top: 100 }}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
        >
          <Form.Item
            name="oldPassword"
            label="Current Password"
            rules={[{ required: true, message: 'Please enter your current password' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm New Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="twoFaCode"
            label="2FA Code"
            rules={[
              { required: true, message: 'Please enter your 2FA code' },
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
            ]}
          >
            <Input maxLength={6} placeholder="000000" />
          </Form.Item>

          <Form.Item>
            <Space>
              <LoadingButton type="primary" htmlType="submit" loading={loading || apiLoading}>
                Change Password
              </LoadingButton>
              <LoadingButton onClick={() => {
                setChangePasswordVisible(false);
                passwordForm.resetFields();
              }} useGlobalApiLoading>
                Cancel
              </LoadingButton>
            </Space>
          </Form.Item>
        </Form>
      </StyledModal>

      {/* Reset 2FA Modal */}
      <StyledModal
        title="Reset Two-Factor Authentication"
        visible={reset2FAVisible}
        onCancel={() => {
          setReset2FAVisible(false);
          reset2FAForm.resetFields();
        }}
        footer={null}
        style={{ top: 100 }}
      >
        <div style={{ marginBottom: 16, color: '#ffa940' }}>
          <Text>
            Warning: Resetting 2FA will disable two-factor authentication on your account. 
            You will need to set it up again after logging in.
          </Text>
        </div>
        <Form
          form={reset2FAForm}
          layout="vertical"
          onFinish={handleReset2FA}
        >
          <Form.Item
            name="twoFaCode"
            label="Current 2FA Code"
            rules={[
              { required: true, message: 'Please enter your current 2FA code' },
              { pattern: /^\d{6}$/, message: 'Please enter a valid 6-digit code' },
            ]}
          >
            <Input maxLength={6} placeholder="000000" />
          </Form.Item>

          <Form.Item>
            <Space>
              <LoadingButton type="primary" danger htmlType="submit" loading={loading || apiLoading}>
                Reset 2FA
              </LoadingButton>
              <LoadingButton onClick={() => {
                setReset2FAVisible(false);
                reset2FAForm.resetFields();
              }} useGlobalApiLoading>
                Cancel
              </LoadingButton>
            </Space>
          </Form.Item>
        </Form>
      </StyledModal>
    </ProfileContainer>
  );
};

export default Profile;
